import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { reportsApi, trackerApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, ArrowRight, Calendar, Flame, Apple, Pill, Dumbbell } from 'lucide-react';
import { DASHBOARD } from '@/constants/testIds';
import ReminderPrompt from '@/components/ReminderPrompt';
import MorphingAvatarHero from '@/components/MorphingAvatarHero';

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login'); return; }
    reportsApi.list().then(setReports).catch((e) => {
      toast.error(e.response?.data?.detail || 'Could not load history');
    }).finally(() => setLoading(false));
    trackerApi.summary().then(setSummary).catch(() => {});
  }, [user, authLoading, nav]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16" data-testid={DASHBOARD.container}>
      <ReminderPrompt />
      {user && <MorphingAvatarHero userName={user.name?.split(' ')[0]} />}
      {summary && (
        <div className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">This week &middot; {summary.week_start} → {summary.week_end}</span>
            <Link to="/tracker" className="text-sm font-semibold text-[#4A7C59] hover:underline">Open tracker →</Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Apple, label: 'Calories today', value: Math.round(summary.calorie_today || 0), goal: summary.goals.calorie_goal, accent: '#4A7C59' },
              { icon: Pill, label: 'Vitamins this week', value: Math.round(summary.vitamins_week || 0), goal: summary.goals.vitamin_weekly_goal, accent: '#7E9979' },
              { icon: Dumbbell, label: 'Workouts this week', value: summary.gym_week || 0, goal: summary.goals.gym_weekly_goal, accent: '#C05746' },
            ].map((s, i) => {
              const pct = s.goal ? Math.min(100, Math.round((s.value / s.goal) * 100)) : 0;
              return (
                <div key={i} className="bg-white border border-[#E5E1D8] rounded-2xl p-5" data-testid="dashboard-week-card">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">
                    <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: s.accent }} /> {s.label}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-serif-display text-4xl font-medium text-[#1A2421] tabular-nums">{s.value}</span>
                    <span className="text-sm text-[#4A5A53]">/ {s.goal}</span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-[#E5E1D8] overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: s.accent }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div className="flex-1 min-w-[260px]">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">Your history</span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#1A2421] font-medium leading-tight">
            {user ? `Welcome back, ${user.name.split(' ')[0]}.` : ''}
          </h1>
        </div>
        <Link to="/intake">
          <Button className="rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white px-6 gap-2" data-testid={DASHBOARD.newReportButton}>
            <Plus className="w-4 h-4" strokeWidth={1.5} /> New intake
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-[#4A5A53] font-light">Loading your journey…</div>
      ) : reports.length === 0 ? (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-12 text-center">
          <p className="font-serif-display text-2xl text-[#1A2421] font-medium">No reports yet.</p>
          <p className="text-[#4A5A53] mt-2 font-light">Take your first intake to see your personalized plan.</p>
          <Link to="/intake">
            <Button className="mt-6 rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white gap-2 px-6">
              Start intake <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((rep) => {
            const date = new Date(rep.created_at);
            const short = rep.report?.greeting?.slice(0, 100) || 'Your Wellth plan';
            return (
              <Link
                to={`/reports/${rep.id}`}
                key={rep.id}
                className="bg-white border border-[#E5E1D8] rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-sm block"
                data-testid={DASHBOARD.reportCard}
              >
                <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#7E9979]">
                  <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <p className="mt-4 font-serif-display text-xl text-[#1A2421] font-medium leading-snug">{short}…</p>
                <div className="mt-5 flex items-center gap-3 text-xs text-[#4A5A53]">
                  <span>Age {rep.intake.age}</span>
                  <span>·</span>
                  <span>{rep.intake.weight_lbs} lbs</span>
                  <span>·</span>
                  <span>{rep.intake.health_issues?.length || 0} concerns</span>
                </div>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A7C59]">
                  Open plan <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
